import { cloneElement, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
    Badge,
    Button,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    useScrollTrigger
} from "@mui/material";
import DashboardIcon from '@mui/icons-material/Dashboard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SettingsIcon from '@mui/icons-material/Settings';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuth } from "../../store/auth-context";
import { usePackageModal } from "../../store/modal-context";
import cancelButtonSx from "../../styles/cancelButtonSx";
import WebOnly from "../platforms/WebOnly";
import AppOnly from "../platforms/AppOnly";
import PersonIcon from '@mui/icons-material/Person';
import PaidIcon from '@mui/icons-material/Paid';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useNotification } from "../../store/notification-context";
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import UsageProgress from "../UsageProgress";
import StorageUsageProgress from "../StorageUsageProgress";
import useApiHook from "../../hooks/useApiHook";

const useNotificationsMenuController = () => {
    const [anchorElNotifications, setAnchorElNotifications] = useState(null);
    const { loadInboxNotifications } = useNotification();

    const openNotificationsMenu = async (event) => {
        setAnchorElNotifications(event.currentTarget);
        await loadInboxNotifications();
    };

    const closeNotificationsMenu = () => {
        setAnchorElNotifications(null);
    };

    return {
        anchorElNotifications,
        openNotificationsMenu,
        closeNotificationsMenu
    };
};

const buildNavigationItems = ({ isVerifiedAdmin, openModal }) => [
    {
        icon: <AdminPanelSettingsIcon />,
        title: 'Admin Dashboard',
        link: '/admin-dashboard',
        hidden: !isVerifiedAdmin
    },
    {
        icon: <DashboardIcon />,
        title: 'Dashboard File',
        link: '/dashboard',
    },
    {
        icon: <SettingsIcon />,
        title: 'Impostazioni',
        children: [
            {
                icon: <PersonIcon fontSize="small" />,
                title: 'Scheda Psicologo',
                link: '/scheda-psicologo',
            },
            {
                icon: <NotificationsNoneIcon fontSize="small" />,
                title: 'Notifiche Trascrizione',
                link: '/notifiche-trascrizione',
            },
            {
                icon: <ShieldOutlinedIcon fontSize="small" />,
                title: 'Sicurezza e Privacy',
                action: 'security',
                keepDrawerOpen: true
            },
            {
                icon: <DescriptionOutlinedIcon fontSize="small" />,
                title: 'Termini e Condizioni',
                href: 'https://www.sbobbi.com/termini-e-condizioni/'
            },
            {
                icon: <PersonOffIcon fontSize="small" />,
                title: 'Elimina Profilo',
                action: 'delete-profile',
                keepDrawerOpen: true
            }
        ]
    },
    {
        icon: <PaidIcon />,
        title: 'Acquista Pacchetti',
        onClick: () => {
            openModal();
        }
    },
    {
        icon: <ReceiptLongIcon />,
        title: 'Pagamenti e fatture',
        link: '/billing',
    }
];

const SecurityPrivacyDialog = ({ open, onClose }) => {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    pr: 1.5
                }}
            >
                <Stack direction="row" spacing={1.25} alignItems="center">
                    <ShieldOutlinedIcon color="primary" />
                    <Typography component="span" variant="h6" fontWeight={700}>
                        Sicurezza e Privacy
                    </Typography>
                </Stack>
                <IconButton aria-label="chiudi finestra sicurezza e privacy" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2.5}>
                    <Typography variant="body1">
                        La sicurezza dei dati clinici &egrave; il cuore della nostra piattaforma. Sbobbi &egrave; progettato per garantire il massimo livello di protezione previsto dalle normative europee, offrendo a psicologi e professionisti della salute mentale un ambiente affidabile, conforme e costruito per tutelare la riservatezza dei pazienti.
                    </Typography>
                    <Stack spacing={1}>
                        <Typography variant="subtitle1" fontWeight={700}>
                            Valori chiave
                        </Typography>
                        <Typography variant="body2">
                            - <strong>Conformit&agrave; GDPR:</strong> Sbobbi rispetta il GDPR e adotta le misure richieste dall&apos;Unione Europea per il trattamento sicuro dei dati sensibili.
                        </Typography>
                        <Typography variant="body2">
                            - <strong>Massima privacy dati pazienti:</strong> Con Sbobbi puoi oscurare i dati personali nelle trascrizioni ed i file audio vengono eliminati subito dopo la trascrizione, per ridurre al minimo i rischi per la privacy.
                        </Typography>
                        <Typography variant="body2">
                            - <strong>Crittografia avanzata:</strong> I dati sono protetti con crittografia durante trasmissione e archiviazione. Server europei con data center in Italia, conformit&agrave; GDPR e nessun trasferimento extra UE.
                        </Typography>
                        <Typography variant="body2">
                            - <strong>Partner tecnologico certificato:</strong> Per le trascrizioni utilizziamo un partner con standard di sicurezza elevati, certificazioni internazionali e infrastrutture per il trattamento sicuro dei dati sensibili.
                        </Typography>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Chiudi</Button>
            </DialogActions>
        </Dialog>
    );
};

const NavigationDrawer = ({ open, onClose, menuItems, logout }) => {
    const [expandedSection, setExpandedSection] = useState(null);
    const [isSecurityDialogOpen, setIsSecurityDialogOpen] = useState(false);
    const [isDeleteProfileDialogOpen, setIsDeleteProfileDialogOpen] = useState(false);
    const [isDeletingProfile, setIsDeletingProfile] = useState(false);
    const [deleteProfileError, setDeleteProfileError] = useState('');
    const location = useLocation();
    const { openModal } = usePackageModal();
    const { makeRequest } = useApiHook();
    const pastelBlueHover = '#dceeff';
    const pastelBlueSelected = '#c2dbf5';
    const baseButtonSx = {
        color: '#111111',
        '& .MuiListItemText-primary': {
            color: '#111111',
        },
        '& .MuiListItemIcon-root': {
            color: '#111111',
        },
        '&:hover': {
            backgroundColor: pastelBlueHover
        }
    };

    const toggleSection = (title) => {
        setExpandedSection((currentSection) => currentSection === title ? null : title);
    };

    const renderLeafItem = ({ title, icon, link, href, onClick, action, keepDrawerOpen = false }, nested = false) => {
        const buttonSx = nested ? { ...baseButtonSx, pl: 4 } : baseButtonSx;
        const iconSx = nested ? { minWidth: 36 } : undefined;

        if (link) {
            return (
                <ListItem key={title} disablePadding>
                    <NavLink
                        to={link}
                        style={{
                            textDecoration: 'none',
                            width: '100%'
                        }}
                    >
                        {({ isActive }) => (
                            <ListItemButton
                                sx={{
                                    ...buttonSx,
                                    ...(isActive ? {
                                        backgroundColor: pastelBlueSelected,
                                        '&:hover': {
                                            backgroundColor: pastelBlueSelected,
                                        }
                                    } : {})
                                }}
                                onClick={onClose}
                            >
                                <ListItemIcon sx={iconSx}>
                                    {icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={title}
                                    primaryTypographyProps={{ fontWeight: isActive ? 700 : 500 }}
                                />
                            </ListItemButton>
                        )}
                    </NavLink>
                </ListItem>
            );
        }

        if (href) {
            return (
                <ListItem key={title} disablePadding>
                    <ListItemButton
                        sx={buttonSx}
                        onClick={() => {
                            window.open(href, '_blank', 'noopener,noreferrer');
                            onClose();
                        }}
                    >
                        <ListItemIcon sx={iconSx}>
                            {icon}
                        </ListItemIcon>
                        <ListItemText primary={title} />
                    </ListItemButton>
                </ListItem>
            );
        }

        return (
            <ListItem key={title} disablePadding>
                <ListItemButton
                    sx={buttonSx}
                    onClick={(event) => {
                        if (action === 'security') {
                            setIsSecurityDialogOpen(true);
                        }
                        if (action === 'delete-profile') {
                            setDeleteProfileError('');
                            setIsDeleteProfileDialogOpen(true);
                        }

                        onClick?.(event);

                        if (!keepDrawerOpen && action !== 'security' && action !== 'delete-profile') {
                            onClose();
                        }
                    }}
                >
                    <ListItemIcon sx={iconSx}>
                        {icon}
                    </ListItemIcon>
                    <ListItemText primary={title} />
                </ListItemButton>
            </ListItem>
        );
    };

    const handleDeleteProfile = async () => {
        setIsDeletingProfile(true);
        setDeleteProfileError('');

        const result = await makeRequest('/auth/profile', 'delete', {});

        setIsDeletingProfile(false);

        if (result?.state === 'success') {
            setIsDeleteProfileDialogOpen(false);
            onClose();
            logout();
            return;
        }

        setDeleteProfileError(result?.error?.message || "Non siamo riusciti a eliminare il profilo.");
    };

    return (
        <Drawer open={open} onClose={onClose}>
            <>
                <Box sx={{ width: 280 }} role="presentation">
                    <List>
                        {menuItems.filter(({ hidden }) => !hidden).map((item) => {
                            if (!item.children?.length) {
                                return renderLeafItem(item);
                            }

                            const hasActiveChild = item.children.some((child) => child.link && location.pathname === child.link);
                            const isExpanded = expandedSection === item.title || hasActiveChild;
                            const sectionButtonSx = isExpanded
                                ? {
                                    ...baseButtonSx,
                                    borderLeft: '4px solid #8fb8e8',
                                    pl: 1.5,
                                    '& .MuiListItemText-primary': {
                                        color: '#111111',
                                        fontWeight: 700
                                    },
                                    '& .MuiListItemIcon-root': {
                                        color: '#111111'
                                    }
                                }
                                : baseButtonSx;

                            return (
                                <Box key={item.title}>
                                    <ListItem disablePadding>
                                        <ListItemButton
                                            onClick={() => toggleSection(item.title)}
                                            sx={sectionButtonSx}
                                        >
                                            <ListItemIcon>
                                                {item.icon}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={item.title}
                                                primaryTypographyProps={{ fontWeight: isExpanded ? 700 : 500 }}
                                            />
                                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </ListItemButton>
                                    </ListItem>
                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                        <List disablePadding>
                                            {item.children.map((child) => renderLeafItem(child, true))}
                                        </List>
                                    </Collapse>
                                </Box>
                            );
                        })}
                    </List>
                    <Divider />
                    <Box sx={{ p: 2 }}>
                        <UsageProgress showAddCreditsButton={false} />
                        <StorageUsageProgress />
                        <Box mt={1.5} display="flex" justifyContent="center">
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={openModal}
                                sx={{
                                    width: 'fit-content',
                                    fontSize: '0.8rem',
                                    fontWeight: 700,
                                    px: 1.5,
                                    py: 0.5,
                                    minHeight: 30
                                }}
                            >
                                Aggiungi ore extra
                            </Button>
                        </Box>
                    </Box>
                    <List sx={{ pt: 0, pb: 1 }}>
                        <ListItem disablePadding>
                            <ListItemButton
                                sx={baseButtonSx}
                                onClick={() => {
                                    logout();
                                    onClose();
                                }}
                            >
                                <ListItemIcon>
                                    <LogoutIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Logout"
                                    primaryTypographyProps={{ fontWeight: 500 }}
                                />
                            </ListItemButton>
                        </ListItem>
                    </List>
                </Box>
                <SecurityPrivacyDialog
                    open={isSecurityDialogOpen}
                    onClose={() => {
                        setIsSecurityDialogOpen(false);
                        onClose();
                    }}
                />
                <Dialog
                    open={isDeleteProfileDialogOpen}
                    onClose={isDeletingProfile ? undefined : () => setIsDeleteProfileDialogOpen(false)}
                    fullWidth
                    maxWidth="xs"
                >
                    <DialogTitle>Elimina Profilo</DialogTitle>
                    <DialogContent dividers>
                        <Stack spacing={2}>
                            <Typography variant="body2">
                                Questa azione elimina definitivamente il tuo account, i file, le trascrizioni e tutti i dati associati.
                            </Typography>
                            <Typography variant="body2" color="error">
                                L&apos;operazione e&apos; irreversibile.
                            </Typography>
                            {deleteProfileError ? (
                                <Typography variant="body2" color="error">
                                    {deleteProfileError}
                                </Typography>
                            ) : null}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" onClick={() => setIsDeleteProfileDialogOpen(false)} disabled={isDeletingProfile} sx={cancelButtonSx}>
                            Annulla
                        </Button>
                        <Button color="error" variant="contained" onClick={handleDeleteProfile} disabled={isDeletingProfile}>
                            {isDeletingProfile ? 'Eliminazione...' : 'Elimina Profilo'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        </Drawer>
    );
};

const NavigationMenuButton = ({ onClick, sx = {}, iconSx = {} }) => (
    <Box>
        <IconButton
            size="large"
            aria-label="open navigation menu"
            aria-controls="navigation-drawer"
            aria-haspopup="true"
            onClick={onClick}
            color="inherit"
            sx={sx}
        >
            <MenuIcon sx={iconSx} />
        </IconButton>
    </Box>
);

const NotificationsMenu = ({ controller }) => {
    const { anchorElNotifications, openNotificationsMenu, closeNotificationsMenu } = controller;
    const {
        unreadCount,
        inboxNotifications,
        deleteInboxNotification,
        clearInboxNotifications
    } = useNotification();
    const navigate = useNavigate();

    const openFileNotification = async (notificationId, fileId) => {
        await deleteInboxNotification(notificationId);
        closeNotificationsMenu();

        if (fileId) {
            navigate(`/files/${fileId}`);
        }
    };

    const transcriptionNotifications = inboxNotifications.filter(
        (notification) => notification.type === 'transcription_completed'
    );
    const otherNotifications = inboxNotifications.filter(
        (notification) => notification.type !== 'transcription_completed'
    );

    return (
        <Box sx={{ flexGrow: 0, mr: 1 }}>
            <Tooltip title="Notifiche">
                <IconButton color="inherit" onClick={openNotificationsMenu}>
                    <Badge badgeContent={unreadCount} color="error">
                        <NotificationsNoneIcon />
                    </Badge>
                </IconButton>
            </Tooltip>
            <Menu
                sx={{ mt: '45px' }}
                anchorEl={anchorElNotifications}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={Boolean(anchorElNotifications)}
                onClose={closeNotificationsMenu}
            >
                {inboxNotifications.length === 0 && (
                    <MenuItem disabled>
                        <Typography sx={{ textAlign: 'center' }}>Nessuna notifica</Typography>
                    </MenuItem>
                )}
                {transcriptionNotifications.length > 0 && (
                    <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
                        <Typography fontWeight='bold'>Trascrizioni completate:</Typography>
                    </Box>
                )}
                {transcriptionNotifications.map((notification) => (
                    <MenuItem
                        key={notification.id}
                        onClick={() => openFileNotification(notification.id, notification.fileId)}
                    >
                        <Typography variant='body2'>{notification.message}</Typography>
                    </MenuItem>
                ))}
                {otherNotifications.map((notification) => (
                    <MenuItem
                        key={notification.id}
                        onClick={() => openFileNotification(notification.id, notification.fileId)}
                    >
                        <Stack spacing={0.5}>
                            <Typography fontWeight='bold'>{notification.title}</Typography>
                            <Typography variant='body2'>{notification.message}</Typography>
                        </Stack>
                    </MenuItem>
                ))}
                {inboxNotifications.length > 0 && (
                    <MenuItem onClick={clearInboxNotifications}>
                        <Typography color="error">Svuota notifiche</Typography>
                    </MenuItem>
                )}
            </Menu>
        </Box>
    );
};

const MobileNavigation = () => {
    const [open, setOpen] = useState(false);
    const toggleDrawer = (newOpen) => () => {
        setOpen(newOpen);
    };
    const navigate = useNavigate();
    const { openModal } = usePackageModal();
    const { logout, user } = useAuth();
    const isVerifiedAdmin = user?.role === 'admin' && user?.adminSessionActive === true;
    const notificationsController = useNotificationsMenuController();
    const menuItems = buildNavigationItems({
        isVerifiedAdmin,
        openModal,
    });

    return <>
        <NavigationDrawer open={open} onClose={toggleDrawer(false)} menuItems={menuItems} logout={logout} />
        <Box sx={{ background: (theme) => theme.palette.primary.main, display: 'flex' }}>
            <Container maxWidth="xl">
                <Toolbar disableGutters>
                    <NavigationMenuButton onClick={toggleDrawer(true)} />
                    <Stack alignItems={'center'} justifyContent={'center'} sx={{ flexGrow: 1 }}>
                        <img alt="Logo Sbobbi"
                            height="40"
                            src="/logo-hor-white.svg" />
                    </Stack>
                    {isVerifiedAdmin ? (
                        <Chip
                            size="small"
                            label="Admin"
                            color="warning"
                            clickable
                            onClick={() => navigate('/admin-dashboard')}
                            sx={{ mr: 1, cursor: 'pointer' }}
                        />
                    ) : null}
                    <NotificationsMenu controller={notificationsController} />
                </Toolbar>
            </Container>
        </Box>
    </>;
};

const DesktopNavigation = () => {
    const [open, setOpen] = useState(false);
    const toggleDrawer = (newOpen) => () => {
        setOpen(newOpen);
    };
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { openModal } = usePackageModal();
    const isVerifiedAdmin = user?.role === 'admin' && user?.adminSessionActive === true;
    const notificationsController = useNotificationsMenuController();
    const menuItems = buildNavigationItems({
        isVerifiedAdmin,
        openModal,
    });

    return <>
        <NavigationDrawer open={open} onClose={toggleDrawer(false)} menuItems={menuItems} logout={logout} />
        <Stack>
            <Container maxWidth="lg" fixed sx={{ boxSizing: 'border-box', px: 0 }}>
                <Toolbar disableGutters sx={{ minHeight: 64 }}>
                    <NavigationMenuButton
                        onClick={toggleDrawer(true)}
                        sx={{ ml: 0.5 }}
                        iconSx={{ fontSize: 34 }}
                    />
                    <Stack alignItems={'center'} justifyContent={'center'} mr={2} ml={3}>
                        <img
                            alt="Logo Sbobbi"
                            height="32"
                            src="/logo-hor-white.svg"
                            onClick={() => navigate('/dashboard')}
                            style={{ cursor: 'pointer' }}
                        />
                    </Stack>
                    <Box sx={{ flexGrow: 1 }} />
                    {isVerifiedAdmin ? (
                        <Chip
                            size="small"
                            label="Admin"
                            color="warning"
                            clickable
                            onClick={() => navigate('/admin-dashboard')}
                            sx={{ mr: 2, color: 'white', borderColor: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}
                            variant="outlined"
                        />
                    ) : null}
                    <NotificationsMenu controller={notificationsController} />
                </Toolbar>
            </Container>
        </Stack>
    </>;
};

const Navigation = () => {
    const ElevationScroll = (props) => {
        const { children } = props;
        const trigger = useScrollTrigger({
            disableHysteresis: true,
            threshold: 0,
        });

        return children
            ? cloneElement(children, {
                elevation: trigger ? 1 : 0,
            })
            : null;
    };

    return (
        <>
            <WebOnly>
                <AppBar position="sticky">
                    <DesktopNavigation />
                </AppBar>
            </WebOnly>
            <AppOnly>
                <ElevationScroll>
                    <AppBar>
                        <MobileNavigation />
                    </AppBar>
                </ElevationScroll>
                <Box height={56} />
            </AppOnly>
        </>

    );
};

export default Navigation;
